/** Credits screen: asset and audio credits from the public CREDITS.json files, code notices, fonts, brand. */
import notices from '../../THIRD_PARTY_NOTICES.md?raw';
import { h, icon } from './dom';
import { topBar } from './screens';
import type { GameUi } from './ui';

interface Credit { title: string; author: string; license: string; url?: string; use?: string }

const BASE = import.meta.env.BASE_URL;
let cache: { assets: Credit[]; audio: Credit[] } | null = null;

async function loadCredits(): Promise<{ assets: Credit[]; audio: Credit[] }> {
  if (cache) return cache;
  const get = async (p: string): Promise<Record<string, string | number>[]> => {
    try { return (await (await fetch(`${BASE}${p}`)).json()) as Record<string, string | number>[]; } catch { return []; }
  };
  const [a, s] = await Promise.all([get('assets/CREDITS.json'), get('audio/CREDITS.json')]);
  const norm = (r: Record<string, string | number>): Credit => ({
    title: String(r.title ?? r.file ?? ''), author: String(r.author ?? ''), license: String(r.license ?? r.licence ?? ''),
    url: r.source ? String(r.source) : r.url ? String(r.url) : undefined, use: r.use ? String(r.use) : r.file ? String(r.file) : undefined,
  });
  cache = { assets: a.map(norm), audio: s.map(norm) };
  return cache;
}

/** "## Name (Licence)" headings of THIRD_PARTY_NOTICES.md, with the first paragraph under each. */
export function noticeSections(md: string): { name: string; text: string }[] {
  const out: { name: string; text: string }[] = [];
  const parts = md.split(/^## /m).slice(1);
  for (const p of parts) {
    const [head, ...rest] = p.split('\n');
    const body = rest.join('\n').split(/```/)[0]!.trim().replace(/\s+/g, ' ');
    out.push({ name: head!.trim(), text: body });
  }
  return out;
}

const FONTS: Credit[] = [
  { title: 'Russo One', author: 'Jovanny Lemonad', license: 'SIL OFL 1.1', url: 'https://fonts.google.com/specimen/Russo+One' },
  { title: 'Exo 2', author: 'Natanael Gama', license: 'SIL OFL 1.1', url: 'https://fonts.google.com/specimen/Exo+2' },
];
const LIBS: Credit[] = [
  { title: 'three.js', author: 'three.js authors', license: 'MIT', url: 'https://threejs.org' },
  { title: 'meshoptimizer', author: 'Arseny Kapoulkine', license: 'MIT', url: 'https://github.com/zeux/meshoptimizer' },
];

function list(ui: GameUi, items: Credit[]): HTMLElement {
  return h('ul.credit-list', {}, items.map((c) => h('li', {}, [
    h('div.credit-title', {}, [c.url ? h('a', { text: c.title, attrs: { href: c.url, target: '_blank', rel: 'noopener', 'data-nav': '' } }) : h('span', { text: c.title })]),
    h('div.credit-meta', {}, [
      c.author ? h('span', { text: ui.tr('credits.by', { author: c.author }) }) : null,
      c.license ? h('span.lic', { text: c.license.replace(/\s*\(.*\)$/, '') }) : null,
    ]),
    !c.author && c.use ? h('p.credit-use', { text: c.use.length > 220 ? `${c.use.slice(0, 217)}...` : c.use }) : null,
  ])));
}

export function buildCredits(ui: GameUi): HTMLElement {
  const assets = h('div.credit-slot'), audio = h('div.credit-slot');
  void loadCredits().then((c) => { assets.replaceChildren(list(ui, c.assets)); audio.replaceChildren(list(ui, c.audio)); });
  const code = noticeSections(notices).map((n) => ({ title: n.name, author: '', license: '', use: n.text }));
  return h('section.screen.credits', {}, [
    topBar(ui, ui.tr('ui.credits')),
    h('div.panel.glass.credits-panel', {}, [
      h('p.lead', { text: ui.tr('credits.made') }),
      h('div.credit-cols', {}, [
        h('div', {}, [h('h3.section', {}, [icon('garage'), h('span', { text: ui.tr('credits.models') })]), assets]),
        h('div', {}, [h('h3.section', {}, [icon('sound'), h('span', { text: ui.tr('credits.audio') })]), audio]),
        h('div', {}, [
          h('h3.section', {}, [icon('keyboard'), h('span', { text: ui.tr('credits.code') })]), list(ui, [...LIBS, ...code]),
          h('h3.section', {}, [icon('info'), h('span', { text: ui.tr('credits.fonts') })]), list(ui, FONTS),
          h('h3.section', {}, [icon('flag'), h('span', { text: ui.tr('credits.brand') })]), h('p.muted', { text: ui.tr('credits.brandText') }),
        ]),
      ]),
    ]),
  ]);
}
